import React, { useState } from "react";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const fmtMY = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

const fmtRange = (start, end) => {
  const s = fmtMY(start);
  const e = end ? fmtMY(end) : "Present";
  if (!s && !e) return "";
  if (!s) return e;
  return `${s} — ${e}`;
};

const tenureMonths = (start, end) => {
  if (!start) return null;
  const s = new Date(start);
  const e = end ? new Date(end) : new Date();
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return null;
  return (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth());
};

const fmtTenure = (months) => {
  if (months == null || months < 0) return "";
  if (months < 12) return `${months} mo`;
  const y = Math.floor(months / 12);
  const m = months % 12;
  return m ? `${y}y ${m}mo` : `${y}y`;
};

// Each entry in `position[]` may have a top-level role or a nested `position[]`
// array (multiple roles within the same company). Flatten so each rendered row
// is one role with a clear company + title + dates + optional description.
const flatten = (positions) => {
  if (!Array.isArray(positions)) return [];
  const rows = [];
  for (const p of positions) {
    const company = p?.company || {};
    const nested = Array.isArray(p?.position) ? p.position : null;
    if (nested && nested.length) {
      for (const n of nested) {
        rows.push({
          companyName: company.name || n.company || "",
          companyLogo: company.logo || "",
          companyUrl: company.url || "",
          title: n.title || "",
          start: n?.date?.start || n.start,
          end: n?.date?.end || n.end,
          description: n.description || "",
          location: n.location || ""
        });
      }
    } else {
      rows.push({
        companyName: company.name || p.company || "",
        companyLogo: company.logo || "",
        companyUrl: company.url || "",
        title: p.title || "",
        start: p?.date?.start || p.start,
        end: p?.date?.end || p.end,
        description: p.description || "",
        location: p.location || ""
      });
    }
  }
  rows.sort((a, b) => new Date(b.start || 0) - new Date(a.start || 0));
  return rows;
};

// Group consecutive same-company rows so promotion-style progressions render
// under a single company logo with the moves nested as branches. Each group
// is { companyName, companyLogo, companyUrl, roles: [...] }.
const groupByCompany = (rows) => {
  const groups = [];
  for (const r of rows) {
    const last = groups[groups.length - 1];
    if (last && last.companyName && last.companyName === r.companyName) {
      last.roles.push(r);
    } else {
      groups.push({
        companyName: r.companyName,
        companyLogo: r.companyLogo,
        companyUrl: r.companyUrl,
        roles: [r]
      });
    }
  }
  return groups;
};

const Logo = ({ src, name }) => {
  if (src) return <img src={src} alt="" className="drCareer__logo" />;
  return (
    <div className="drCareer__logoFallback">
      {(name || "?").slice(0, 1).toUpperCase()}
    </div>
  );
};

const RoleNode = ({ role, isCurrent, rowOpen, onToggle }) => {
  const tenure = fmtTenure(tenureMonths(role.start, role.end));
  const hasDesc = role.description && role.description.trim().length > 0;
  return (
    <div className={`drCareer__role ${isCurrent ? "drCareer__role--current" : ""}`}>
      <div className="drCareer__roleLine">
        <span className="drCareer__roleTitle">{role.title || "—"}</span>
        {isCurrent && <span className="drCareer__currentBadge">Current</span>}
      </div>
      <div className="drCareer__roleMeta">
        <span>{fmtRange(role.start, role.end)}</span>
        {tenure && (
          <>
            <span className="drCareer__dot">·</span>
            <span className="drCareer__tenure">{tenure}</span>
          </>
        )}
        {role.location && (
          <>
            <span className="drCareer__dot">·</span>
            <span>{role.location}</span>
          </>
        )}
      </div>
      {hasDesc && (
        <>
          <button
            type="button"
            className="drCareer__expandBtn"
            onClick={onToggle}
            aria-expanded={rowOpen}
          >
            {rowOpen ? "Hide details" : "Show details"}
          </button>
          {rowOpen && <p className="drCareer__desc">{role.description}</p>}
        </>
      )}
    </div>
  );
};

const CareerTimeline = ({ positions, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [openRows, setOpenRows] = useState({});

  const rows = flatten(positions);
  if (rows.length === 0) return null;

  const groups = groupByCompany(rows);
  const totalRoles = rows.length;

  return (
    <div className="drSection drCareer">
      <button
        type="button"
        className="drCareer__header"
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
      >
        <i className="material-icons-outlined drCareer__chevron">
          {open ? "expand_more" : "chevron_right"}
        </i>
        <span className="drSectionIcon">💼</span>
        <h3 className="drCareer__title">Career Timeline</h3>
        <span className="drCareer__count">{totalRoles}</span>
      </button>

      {open && (
        <div className="drCareer__rail">
          {groups.map((g, gi) => {
            const isFirstGroup = gi === 0;
            const isLastGroup = gi === groups.length - 1;
            const groupTotal = g.roles.reduce(
              (sum, r) => sum + (tenureMonths(r.start, r.end) || 0), 0
            );
            const groupTenure = fmtTenure(groupTotal);
            // Roles within group are still sorted desc (most recent first).
            return (
              <div
                key={gi}
                className={`drCareer__group ${isLastGroup ? "drCareer__group--last" : ""}`}
              >
                <div className="drCareer__nodeCol">
                  <div className={`drCareer__node ${isFirstGroup ? "drCareer__node--first" : ""}`}>
                    <Logo src={g.companyLogo} name={g.companyName} />
                  </div>
                  {!isLastGroup && <div className="drCareer__connector" />}
                </div>
                <div className="drCareer__groupBody">
                  <div className="drCareer__companyRow">
                    {g.companyUrl ? (
                      <a
                        className="drCareer__company"
                        href={g.companyUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {g.companyName || "Unknown"}
                      </a>
                    ) : (
                      <span className="drCareer__company">{g.companyName || "Unknown"}</span>
                    )}
                    {g.roles.length > 1 && groupTenure && (
                      <span className="drCareer__groupTenure">
                        {g.roles.length} roles · {groupTenure} total
                      </span>
                    )}
                  </div>
                  <div className="drCareer__roles">
                    {g.roles.map((r, ri) => {
                      const key = `${gi}-${ri}`;
                      // A role is "current" when it has no end date.
                      const isCurrent = !r.end;
                      return (
                        <div key={key} className="drCareer__roleWrap">
                          {g.roles.length > 1 && (
                            <span className="drCareer__subBullet" aria-hidden="true" />
                          )}
                          <RoleNode
                            role={r}
                            isCurrent={isCurrent}
                            rowOpen={!!openRows[key]}
                            onToggle={() =>
                              setOpenRows(s => ({ ...s, [key]: !s[key] }))
                            }
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CareerTimeline;
